'use client';

// Core
import { useState, useTransition } from 'react';

// Vendors
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';

// Global schemas
import { LoginSchema } from '@/schemas';

// Global UI components
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { login } from '@/actions';
import Link from 'next/link';

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FormError, FormSuccess } from '@/components/form';
import { CardWrapper } from '@/features/auth/card-wrapper';
import { useTranslations } from '@/hooks/useTranslations';
import { LoadingSpinner } from '@/components/animations/loading-spinner';

export const LoginForm = () => {
  const searchParams = useSearchParams();

  const urlError =
    searchParams.get('error') === 'OAuthAccountNotLinked'
      ? 'Email already in use with different provider!'
      : '';

  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [error, setError] = useState<string | undefined>('');
  const [success, setSuccess] = useState<string | undefined>('');

  const [isPending, startTransition] = useTransition();

  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const onSubmit = (values: z.infer<typeof LoginSchema>) => {
    setError('');
    setSuccess('');

    startTransition(() => {
      login(values)
        .then((data) => {
          if (data?.error) {
            setError(data.error);
          }

          if (data?.success) {
            form.reset();
            setSuccess(data.success);
          }

          if (data?.twoFactor) {
            setShowTwoFactor(true);
          }
        })
        .catch((error) => {
          // NEXT_REDIRECT means signIn redirected successfully
          // The redirect happens server-side via signIn's redirectTo
          if (error?.digest?.includes('NEXT_REDIRECT')) {
            // Force a hard navigation to ensure session is loaded
            window.location.href = window.location.origin;
            return;
          }

          setError('Something went wrong!');
        });
    });
  };

  const t = useTranslations('LoginForm');

  return (
    <CardWrapper
      title={t('title')}
      headerLabel={t('headerLabel')}
      backButtonLabel={t('backButtonLabel')}
      backButtonHref="/register"
      showSocial={showTwoFactor ? false : true}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {showTwoFactor && (
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Two Factor Code</FormLabel>

                      <FormControl>
                        <InputOTP maxLength={6} {...field}>
                          <InputOTPGroup className="w-1/2">
                            <InputOTPSlot className="w-1/3 h-12" index={0} />
                            <InputOTPSlot className="w-1/3 h-12" index={1} />
                            <InputOTPSlot className="w-1/3 h-12" index={2} />
                          </InputOTPGroup>

                          <InputOTPSeparator />

                          <InputOTPGroup className="w-1/2">
                            <InputOTPSlot className="w-1/3 h-12" index={3} />
                            <InputOTPSlot className="w-1/3 h-12" index={4} />
                            <InputOTPSlot className="w-1/3 h-12" index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </motion.div>

            {!showTwoFactor && (
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('emailLabel')}</FormLabel>

                      <FormControl>
                        <Input
                          {...field}
                          disabled={isPending}
                          placeholder="john.doe@example.com"
                          type="email"
                          className={
                            fieldState.error
                              ? 'border-red-600 focus-visible:ring-red-600'
                              : ''
                          }
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>{t('passwordLabel')}</FormLabel>

                      <FormControl>
                        <Input
                          {...field}
                          disabled={isPending}
                          placeholder="********"
                          type="password"
                          className={
                            fieldState.error
                              ? 'border-red-600 focus-visible:ring-red-600'
                              : ''
                          }
                        />
                      </FormControl>

                      <FormMessage />

                      <Button
                        size="sm"
                        variant="link"
                        className="px-0 font-normal justify-start"
                        asChild
                        aria-label="Forgot password"
                      >
                        <Link href={`/reset-password`}>
                          {t('forgotPassword')}
                        </Link>
                      </Button>
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>

          <FormError message={error || urlError} />
          <FormSuccess message={success} />

          <Button
            type="submit"
            className="w-full"
            disabled={isPending}
            aria-label="Loading"
          >
            {isPending ? (
              <LoadingSpinner />
            ) : showTwoFactor ? (
              t('confirm')
            ) : (
              t('loginButton')
            )}
          </Button>
        </form>
      </Form>
    </CardWrapper>
  );
};
